import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileBarChart, Calendar, Building2 } from "lucide-react";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subWeeks, subMonths, subYears } from "date-fns";

import ProjectOverviewReport from "@/components/reports/ProjectOverviewReport";
import WorkloadReport from "@/components/reports/WorkloadReport";
import AcquisitionReport from "@/components/reports/AcquisitionReport";

export default function Reports() {
  const [period, setPeriod] = useState("month");
  const [acqCustomerId, setAcqCustomerId] = useState("all");
  const [customPeriod, setCustomPeriod] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list(),
  });

  const { data: timeEntries = [] } = useQuery({
    queryKey: ["time-entries"],
    queryFn: () => base44.entities.TimeEntry.list(),
  });

  const { data: candidates = [] } = useQuery({
    queryKey: ["acquisition-candidates"],
    queryFn: () => base44.entities.AcquisitionCandidate.list(),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["acquisition-activities"],
    queryFn: () => base44.entities.AcquisitionActivity.list(),
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices"],
    queryFn: () => base44.entities.Invoice.list(),
  });

  const getPeriodDates = (periodType) => {
    const now = new Date();
    switch (periodType) {
      case "week":
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case "month":
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case "year":
        return { start: startOfYear(now), end: endOfYear(now) };
      case "last_week":
        return { start: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), end: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) };
      case "last_month":
        return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
      case "last_year":
        return { start: startOfYear(subYears(now, 1)), end: endOfYear(subYears(now, 1)) };
      default:
        return customPeriod;
    }
  };

  const { start: startDate, end: endDate } = getPeriodDates(period);

  const periodButtons = [
    { value: "week", label: "Denne uken" },
    { value: "month", label: "Denne måneden" },
    { value: "year", label: "Dette året" },
    { value: "last_week", label: "Forrige uke" },
    { value: "last_month", label: "Forrige måned" },
    { value: "last_year", label: "Forrige år" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-zinc-900 to-zinc-900 flex items-center justify-center">
              <FileBarChart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Rapporter</h1>
              <p className="text-slate-600">Detaljert analyse og rapportering</p>
            </div>
          </div>
        </div>

        <Card className="bg-white border-0 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Velg periode</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {periodButtons.map((btn) => (
              <Button
                key={btn.value}
                variant={period === btn.value ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod(btn.value)}
                className={period === btn.value ? "bg-zinc-900 hover:bg-zinc-900" : ""}
              >
                {btn.label}
              </Button>
            ))}
          </div>
          <div className="mt-4 text-sm text-slate-600">
            Periode: {startDate.toLocaleDateString("no-NO")} - {endDate.toLocaleDateString("no-NO")}
          </div>
        </Card>

        <Tabs defaultValue="projects" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="projects">Prosjektoversikt</TabsTrigger>
            <TabsTrigger value="workload">Arbeidsmengde</TabsTrigger>
            <TabsTrigger value="acquisitions">Oppkjøpskandidater</TabsTrigger>
            <TabsTrigger value="invoicing">Fakturering</TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            <ProjectOverviewReport 
              projects={projects} 
              timeEntries={timeEntries}
              invoices={invoices}
              startDate={startDate}
              endDate={endDate}
            />
          </TabsContent>

          <TabsContent value="workload">
            <WorkloadReport 
              users={users}
              tasks={tasks}
              timeEntries={timeEntries}
              startDate={startDate}
              endDate={endDate}
            />
          </TabsContent>

          <TabsContent value="acquisitions">
            <div className="space-y-4">
              <Card className="p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 shrink-0">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Kunde</span>
                </div>
                <Select value={acqCustomerId} onValueChange={setAcqCustomerId}>
                  <SelectTrigger className="w-full sm:w-80">
                    <SelectValue placeholder="Velg kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle kunder</SelectItem>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400 sm:ml-auto">
                  {(acqCustomerId === "all" ? candidates : candidates.filter((c) => c.customer_id === acqCustomerId)).length} kandidater
                </span>
              </Card>
              <AcquisitionReport
                candidates={acqCustomerId === "all" ? candidates : candidates.filter((c) => c.customer_id === acqCustomerId)}
                activities={activities}
                startDate={startDate}
                endDate={endDate}
                customerName={acqCustomerId === "all" ? null : customers.find((c) => c.id === acqCustomerId)?.company_name}
              />
            </div>
          </TabsContent>

          <TabsContent value="invoicing">
            <Card className="bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900 mb-6">Faktureringsrapport</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-sm text-slate-600 mb-1">Totalt Fakturert</p>
                  <p className="text-2xl font-bold text-green-700">
                    {invoices.reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString()} kr
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {invoices.reduce((sum, inv) => sum + (inv.total_hours || 0), 0).toFixed(1)} timer
                  </p>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-slate-600 mb-1">Betalt</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString()} kr
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {invoices.filter(inv => inv.status === 'paid').length} fakturaer
                  </p>
                </div>

                <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <p className="text-sm text-slate-600 mb-1">Utestående</p>
                  <p className="text-2xl font-bold text-orange-700">
                    {invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled').reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString()} kr
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled').length} fakturaer
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-900">Per Kunde</h3>
                {Object.entries(
                  invoices.reduce((acc, inv) => {
                    if (!acc[inv.customer_name]) {
                      acc[inv.customer_name] = { total: 0, paid: 0, pending: 0, hours: 0 };
                    }
                    acc[inv.customer_name].total += inv.total_amount;
                    acc[inv.customer_name].hours += inv.total_hours || 0;
                    if (inv.status === 'paid') {
                      acc[inv.customer_name].paid += inv.total_amount;
                    } else if (inv.status !== 'cancelled') {
                      acc[inv.customer_name].pending += inv.total_amount;
                    }
                    return acc;
                  }, {})
                ).map(([customerName, stats]) => (
                  <div key={customerName} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-900">{customerName}</h4>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">
                          {stats.total.toLocaleString()} kr
                        </p>
                        <p className="text-xs text-slate-500">{stats.hours.toFixed(1)} timer</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div className="flex-1">
                        <p className="text-slate-600">Betalt: <span className="font-semibold text-green-600">{stats.paid.toLocaleString()} kr</span></p>
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-600">Utestående: <span className="font-semibold text-orange-600">{stats.pending.toLocaleString()} kr</span></p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}